import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Mail, MessageCircle, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { T, BRAND } from "@/content/site";
import { api } from "@/lib/api";
import { buildWhatsappUrl } from "@/lib/whatsapp";

const F = (v) => (v ? "filled" : "");

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "", country: "", whatsapp: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t(T.contact.success));
      setForm({ name: "", company: "", email: "", country: "", whatsapp: "", message: "" });
    } catch (err) {
      toast.error(t(T.contact.error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-[#0F0F0F] py-[140px] md:py-[180px] border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-10 md:gap-16">
          <div className="col-span-12 md:col-span-5">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="overline mb-8">
              <span className="inline-block w-8 h-px bg-[color:var(--sacrvm-gold)] align-middle mr-3" />
              {t(T.contact.overline)}
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="font-serif text-white text-[48px] md:text-[80px] leading-[1] tracking-[-0.01em]"
            >
              {t(T.contact.title)}
            </motion.h2>
            <p className="mt-8 text-[color:var(--sacrvm-text-muted)] text-[16px] leading-[1.7] max-w-[420px] font-serif italic">
              {t(T.contact.subtitle)}
            </p>

            <div className="mt-12 flex flex-col gap-5 text-white/80 text-[14px]">
              <a data-testid="contact-email-link" href={`mailto:${BRAND.email}`} className="flex items-center gap-3 hover:text-[color:var(--sacrvm-gold)] transition-colors">
                <Mail size={16} className="text-[color:var(--sacrvm-gold)]" /> {BRAND.email}
              </a>
              <a
                data-testid="contact-whatsapp"
                href={buildWhatsappUrl(t({ es: "Hola SACRVM, quiero más información.", en: "Hi SACRVM, I'd like more information." }))}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 hover:text-[color:var(--sacrvm-gold)] transition-colors"
              >
                <MessageCircle size={16} className="text-[color:var(--sacrvm-gold)]" /> WhatsApp
              </a>
              <div className="flex items-center gap-3">
                <MapPin size={16} className="text-[color:var(--sacrvm-gold)]" /> {BRAND.location}
              </div>
            </div>

            <div className="mt-10 aspect-[16/10] bg-[#141414] border border-white/10 overflow-hidden" data-testid="contact-map">
              <iframe
                title="Oaxaca map"
                src="https://www.google.com/maps?q=Oaxaca%20de%20Ju%C3%A1rez&z=8&output=embed"
                className="w-full h-full grayscale-[0.6] contrast-125 opacity-90"
                loading="lazy"
              />
            </div>
          </div>

          <div className="col-span-12 md:col-span-7 md:pl-10">
            <form data-testid="contact-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
              <FieldInput testid="contact-name" label={t(T.contact.fields.name)} value={form.name} onChange={set("name")} required />
              <FieldInput testid="contact-company" label={t(T.contact.fields.company)} value={form.company} onChange={set("company")} />
              <FieldInput testid="contact-email" label={t(T.contact.fields.email)} value={form.email} onChange={set("email")} type="email" required />
              <FieldInput testid="contact-country" label={t(T.contact.fields.country)} value={form.country} onChange={set("country")} />
              <FieldInput testid="contact-whatsapp-field" label="WhatsApp" value={form.whatsapp} onChange={set("whatsapp")} />
              <div className="hidden md:block" />
              <div className={"field md:col-span-2 " + F(form.message)}>
                <label>{t(T.contact.fields.message)}</label>
                <textarea data-testid="contact-message" rows={3} value={form.message} onChange={set("message")} required />
              </div>
              <div className="md:col-span-2 mt-10 flex flex-wrap items-center gap-6">
                <button data-testid="contact-submit" disabled={loading} className="btn-sharp gold">
                  {loading ? t({ es: "Enviando…", en: "Sending…" }) : t(T.contact.submit)}
                  <ArrowRight size={14} className="arrow" />
                </button>
                <span className="text-[11px] tracking-[0.24em] uppercase text-white/40">
                  {t({ es: "Respondemos < 24h", en: "We reply < 24h" })}
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function FieldInput({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div className={"field " + F(value)}>
      <label>{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={onChange} required={required} />
    </div>
  );
}
